// src/PartnerDashboard.js
import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [farmersRes, samplesRes] = await Promise.all([
        fetch('/api/farmers/all', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/mrv_samples', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const farmersData = await farmersRes.json();
      const samplesData = await samplesRes.json();

      setFarmers(farmersData.rows || []);
      setSamples(samplesData.rows || []);
      setError("");
    } catch (err) {
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const partners = [
    { 
      name: "AgroReGenerations", 
      logo: "/logos/Twitter-7.2.png", 
      url: "https://agroregenerations.io" 
    },
    { 
      name: "Boma Project", 
      logo: "/logos/Boma image2.jpg", 
      url: "https://bomaproject.org" 
    },
    { 
      name: "FwT AI", 
      logo: "/logos/FwT AI Logo_Transparent.png", 
      url: "https://fwtai.com" 
    },
    { 
      name: "GeoVest", 
      logo: "/logos/geovest-white-1-mk3JGBgeelHPDRpZ.png", 
      url: "https://geovest.com" 
    },
    { 
      name: "M.L Jallow Ecovillage", 
      logo: "/logos/M.L Jallow Ecovillage Logo 171851_All PDF Reader (1).jpg", 
      url: "https://ecovillage.org/ecovillage/ecovillage-with-spirituality/" 
    },
    { 
      name: "TrashRanger", 
      logo: "/logos/TrashRanger.id logo 1.png", 
      url: "https://trashrangersllc.com/" 
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f2419',
      color: 'white',
      padding: '20px'
    }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #2d5a45'
      }}>
        <div>
          <h1 style={{ margin: 0 }}>Partner Dashboard</h1>
          <p style={{ margin: '8px 0 0 0', color: '#888' }}>
            {user?.email} ({user?.role})
          </p>
        </div>
        <button
          onClick={logout}
          style={{
            padding: '10px 20px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Logout
        </button>
      </header>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Navigation Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '30px',
        borderBottom: '1px solid #2d5a45'
      }}>
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            padding: '12px 24px',
            background: activeTab === "overview" ? '#4ade80' : 'transparent',
            color: activeTab === "overview" ? '#0f2419' : '#4ade80',
            border: 'none',
            borderBottom: activeTab === "overview" ? '3px solid #4ade80' : 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("partners")}
          style={{
            padding: '12px 24px',
            background: activeTab === "partners" ? '#4ade80' : 'transparent',
            color: activeTab === "partners" ? '#0f2419' : '#4ade80',
            border: 'none',
            borderBottom: activeTab === "partners" ? '3px solid #4ade80' : 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          Partners
        </button>
        <button
          onClick={() => setActiveTab("farmers")}
          style={{
            padding: '12px 24px',
            background: activeTab === "farmers" ? '#4ade80' : 'transparent',
            color: activeTab === "farmers" ? '#0f2419' : '#4ade80',
            border: 'none',
            borderBottom: activeTab === "farmers" ? '3px solid #4ade80' : 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          Farmers
        </button>
        <button
          onClick={() => setActiveTab("samples")}
          style={{
            padding: '12px 24px',
            background: activeTab === "samples" ? '#4ade80' : 'transparent',
            color: activeTab === "samples" ? '#0f2419' : '#4ade80',
            border: 'none',
            borderBottom: activeTab === "samples" ? '3px solid #4ade80' : 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          Soil Samples
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>System Overview</h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '40px'
              }}>
                <SummaryCard title="Total Farmers" value={farmers.length} color="#4ade80" />
                <SummaryCard title="Total Soil Samples" value={samples.length} color="#60a5fa" />
                <SummaryCard title="Verified Farmers" value={farmers.filter(f => f.verified).length} color="#a78bfa" />
                <SummaryCard title="Verified Samples" value={samples.filter(s => s.verified).length} color="#f472b6" />
              </div>

              <div style={{
                background: '#1a3327',
                padding: '30px',
                borderRadius: '8px',
                border: '1px solid #2d5a45'
              }}>
                <h3 style={{ marginTop: 0 }}>Welcome to the Partner Dashboard</h3>
                <p style={{ color: '#ccc', lineHeight: '1.6' }}>
                  As a partner, you have read-only access to view all farmers and soil sample data across the AgroConnect platform. 
                  Use the tabs above to explore different sections.
                </p>
              </div>
            </div>
          )}

          {/* Partners Tab */}
          {activeTab === "partners" && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>Our Partners</h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {partners.map((partner, idx) => (
                  <div key={idx} style={{
                    background: '#1a3327',
                    padding: '24px',
                    borderRadius: '8px',
                    border: '1px solid #2d5a45',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      margin: '0 auto 16px',
                      background: 'white',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      padding: '10px'
                    }}>
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#4ade80' }}>{partner.name}</h3>
                    <a 
                      href={partner.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        color: '#60a5fa',
                        textDecoration: 'none',
                        fontSize: '14px'
                      }}
                    >
                      Visit Website →
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Farmers Tab */}
          {activeTab === "farmers" && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>All Farmers ({farmers.length})</h2>
              {farmers.length === 0 ? (
                <p style={{ color: '#888' }}>No farmers registered yet</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: '#1a3327',
                    borderRadius: '8px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #2d5a45' }}>
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Country</th>
                        <th style={thStyle}>Region</th>
                        <th style={thStyle}>District</th>
                        <th style={thStyle}>Verified</th>
                        <th style={thStyle}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {farmers.map(farmer => (
                        <tr key={farmer.farmer_id} style={{ borderBottom: '1px solid #2d5a45' }}>
                          <td style={tdStyle}>{farmer.full_name}</td>
                          <td style={tdStyle}>{farmer.email}</td>
                          <td style={tdStyle}>{farmer.country}</td>
                          <td style={tdStyle}>{farmer.region}</td>
                          <td style={tdStyle}>{farmer.district}</td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: farmer.verified ? '#22c55e' : '#ef4444',
                              color: 'white',
                              fontSize: '12px'
                            }}>
                              {farmer.verified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={tdStyle}>{new Date(farmer.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Soil Samples Tab */}
          {activeTab === "samples" && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>All Soil Samples ({samples.length})</h2>
              {samples.length === 0 ? (
                <p style={{ color: '#888' }}>No soil samples submitted yet</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: '#1a3327',
                    borderRadius: '8px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #2d5a45' }}>
                        <th style={thStyle}>Sample ID</th>
                        <th style={thStyle}>Plot ID</th>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Depth (cm)</th>
                        <th style={thStyle}>SOC %</th>
                        <th style={thStyle}>pH</th>
                        <th style={thStyle}>Verified</th>
                        <th style={thStyle}>Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map(sample => (
                        <tr key={sample.sample_id} style={{ borderBottom: '1px solid #2d5a45' }}>
                          <td style={tdStyle}>{sample.sample_id}</td>
                          <td style={tdStyle}>{sample.plot_id}</td>
                          <td style={tdStyle}>{sample.sample_date}</td>
                          <td style={tdStyle}>{sample.sample_depth_cm}</td>
                          <td style={tdStyle}>{sample.soil_organic_carbon_pct}</td>
                          <td style={tdStyle}>{sample.ph}</td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: sample.verified ? '#22c55e' : '#ef4444',
                              color: 'white',
                              fontSize: '12px'
                            }}>
                              {sample.verified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={tdStyle}>{sample.created_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  return (
    <div style={{
      background: '#1a3327',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #2d5a45'
    }}>
      <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: 'bold', color }}>{value}</div>
    </div>
  );
}

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  color: '#4ade80',
  fontWeight: 'bold'
};

const tdStyle = {
  padding: '12px',
  color: '#ccc'
};